// Reglas puras de la Solicitud de Pago de contratos con persona jurídica, lado
// cliente.
//
// ESPEJO de
// src/services/backend/gfunctions/handlePaymentRequestLegal/paymentRequestLogic.js: ese
// paquete de Cloud Functions es un despliegue de Node aparte y no puede
// importar desde src/, así que la lógica está duplicada a propósito. Al cambiar
// una regla, cambiar LAS DOS. paymentRequestLogic.test.js verifica que los
// límites y la regla de vigencia del RUT no se desincronicen.
//
// Aquí las reglas solo dan retroalimentación temprana (deshabilitar el botón,
// explicar qué falta). La autoridad es el backend en SUBMIT_PAYMENT_REQUEST:
// el cliente nunca decide si una solicitud puede enviarse.

import {
  PAYMENT_REQUEST_MAX_FILE_BYTES,
  PAYMENT_REQUEST_MAX_REPORT_BYTES,
  PAYMENT_REQUEST_MAX_ANNEXES,
  PAYMENT_REQUEST_STATUS,
  PERMISSIONS,
  REVIEW_STATUS,
} from "../../../../constants";

const S = PAYMENT_REQUEST_STATUS;

// Máquina de estados de la solicitud (espejo exacto del servidor, que es quien
// la impone). El cliente la consulta solo para saber qué botón pintar.
//   DRAFT ─envía→ SUBMITTED ─devuelve→ IN_CORRECTION ─corrige→ SUBMITTED
//                     └─aprueba→ APPROVED ─recibe→ IN_CAUSATION → IN_ACCOUNTING
//                     → IN_BUDGET → IN_TREASURY → PAYMENT_NOTIFIED → PAID (terminal)
// Contabilidad, Presupuesto y Tesorería devuelven DIRECTO a IN_CAUSATION.
export const PAYMENT_REQUEST_TRANSITIONS = {
  [S.DRAFT]: [S.SUBMITTED],
  [S.SUBMITTED]: [S.IN_CORRECTION, S.APPROVED],
  [S.IN_CORRECTION]: [S.SUBMITTED],
  [S.APPROVED]: [S.IN_CAUSATION],
  [S.IN_CAUSATION]: [S.IN_ACCOUNTING],
  [S.IN_ACCOUNTING]: [S.IN_BUDGET, S.IN_CAUSATION],
  [S.IN_BUDGET]: [S.IN_TREASURY, S.IN_CAUSATION],
  [S.IN_TREASURY]: [S.PAYMENT_NOTIFIED, S.IN_CAUSATION],
  [S.PAYMENT_NOTIFIED]: [S.PAID],
  [S.PAID]: [],
};

export const canTransition = (from, to) => (PAYMENT_REQUEST_TRANSITIONS[from] || []).includes(to);

// Todo lo que el supervisor ya aprobó, esté donde esté dentro de Financiera: es
// lo que cuenta como CAUSADO contra el contrato (ejecución y techo del saldo).
export const POST_APPROVAL_STATUSES = [
  S.APPROVED,
  S.IN_CAUSATION,
  S.IN_ACCOUNTING,
  S.IN_BUDGET,
  S.IN_TREASURY,
  S.PAYMENT_NOTIFIED,
  S.PAID,
];
export const isPostApproval = (status) => POST_APPROVAL_STATUSES.includes(status);

// Lo que compromete saldo: lo causado más lo que está en manos del supervisor.
export const COMMITTING_STATUSES = [...POST_APPROVAL_STATUSES, S.SUBMITTED, S.IN_CORRECTION];

// Lo que muestra la bandeja de Gestión de Pagos sin "incluir pagadas".
export const FINANCIAL_INBOX_STATUSES = POST_APPROVAL_STATUSES.filter((st) => st !== S.PAID);

// El catálogo semilla vive aquí, no en ContractConfigService, para que estas
// reglas sigan siendo importables sin arrastrar la inicialización de Firebase
// (el servicio lo reexporta). Mismo criterio que entityLogic, que EntityService
// importa desde components/.
const PDF_OR_IMAGE = ["application/pdf", "image/*"];

// Semilla y fallback del catálogo administrable
// config/contracts.paymentRequestDocs. Dos clases de entrada:
//   - source: 'request' → la empresa carga el archivo en CADA solicitud.
//   - source: 'entity'  → se resuelve desde el catálogo documental de la
//     entidad y se valida su vigencia (decisión del daily 2026-08-20 sobre el
//     RUT: no pedirlo cinco veces al mes).
// SIN ARL: el flujo jurídico no lo exige (R7).
export const DEFAULT_PAYMENT_REQUEST_DOCS = [
  {
    key: "invoice",
    label: "Factura electrónica de venta",
    enabled: true,
    order: 10,
    required: true,
    source: "request",
    // La factura electrónica colombiana circula como PDF y como XML (DIAN).
    acceptedMime: [...PDF_OR_IMAGE, "application/xml", "text/xml"],
    maxBytes: PAYMENT_REQUEST_MAX_FILE_BYTES,
  },
  {
    key: "social_security",
    label: "Planilla de pago de aportes a seguridad social",
    enabled: true,
    order: 20,
    required: true,
    source: "request",
    acceptedMime: PDF_OR_IMAGE,
    maxBytes: PAYMENT_REQUEST_MAX_FILE_BYTES,
  },
  {
    key: "payroll_taxes",
    label: "Certificado de pago de parafiscales",
    enabled: true,
    order: 30,
    required: true,
    source: "request",
    acceptedMime: PDF_OR_IMAGE,
    maxBytes: PAYMENT_REQUEST_MAX_FILE_BYTES,
  },
  {
    key: "report",
    label: "Informe",
    enabled: true,
    order: 40,
    required: true,
    source: "request",
    acceptedMime: PDF_OR_IMAGE,
    // Tope propio y mucho mayor: un informe de obra puede ser un libro con
    // planos, y no se le exige a la empresa el desglose de actividades del
    // flujo de persona natural.
    maxBytes: PAYMENT_REQUEST_MAX_REPORT_BYTES,
  },
  {
    key: "rut",
    label: "RUT",
    enabled: true,
    order: 50,
    required: true,
    source: "entity",
    entityDocKey: "rut",
    acceptedMime: PDF_OR_IMAGE,
    maxBytes: PAYMENT_REQUEST_MAX_FILE_BYTES,
  },
  {
    key: "no_employees",
    label: "Certificado de No Empleados",
    enabled: true,
    order: 60,
    // Opcional: el daily 2026-08-20 no lo listó entre los que el supervisor
    // revisa obligatoriamente. Queda en el catálogo para no perder el
    // requisito del plan y poder volverlo obligatorio sin desplegar.
    required: false,
    source: "request",
    acceptedMime: PDF_OR_IMAGE,
    maxBytes: PAYMENT_REQUEST_MAX_FILE_BYTES,
  },
];

// Vigencia del RUT: la regla del daily 2026-08-20 es sobre la fecha de
// EXPEDICIÓN del documento (no sobre una fecha de vencimiento declarada), y el
// límite de 3 meses es inclusive.
export const RUT_VALIDITY_MONTHS = 3;

// --- Fechas (calendario, sin husos) -----------------------------------------
// Misma convención que entityLogic: la fecha de un documento es un hecho de
// calendario y viaja como "YYYY-MM-DD"; un Timestamp caería en un día distinto
// según la zona de quien lo lea.

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isValidDate = (value) => {
  if (typeof value !== "string" || !DATE_RE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

export const isoToday = (date = new Date()) => date.toISOString().slice(0, 10);

// Suma meses conservando el día y recortando al último día del mes destino
// (30 de noviembre + 3 meses = 28/29 de febrero).
export const addMonths = (iso, months) => {
  const [y, m, d] = iso.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
};

// Fecha hasta la cual sigue sirviendo un RUT expedido en issuedAt (inclusive).
export const rutValidUntil = (issuedAt) =>
  isValidDate(issuedAt) ? addMonths(issuedAt, RUT_VALIDITY_MONTHS) : null;

// ¿El RUT expedido en issuedAt está vigente a la fecha `today`?
export const isRutValid = (issuedAt, today = isoToday()) => {
  if (!isValidDate(issuedAt) || !isValidDate(today)) return false;
  if (issuedAt > today) return false; // expedición futura: dato inconsistente
  return rutValidUntil(issuedAt) >= today;
};

// Días que le quedan de vigencia (negativo = vencido hace N días), o null si la
// fecha no sirve. Solo para el aviso visual del slot del RUT.
export const daysUntilRutExpiry = (issuedAt, today = isoToday()) => {
  const until = rutValidUntil(issuedAt);
  if (!until || !isValidDate(today)) return null;
  const ms = Date.parse(until + "T00:00:00Z") - Date.parse(today + "T00:00:00Z");
  return Math.round(ms / 86400000);
};

// --- Catálogo ----------------------------------------------------------------

const sorted = (list) => (list || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

// Catálogo vigente: el configurado si existe y trae entradas, la semilla si no.
export const resolvePaymentRequestDocs = (configDocs) => {
  const list =
    Array.isArray(configDocs) && configDocs.length ? configDocs : DEFAULT_PAYMENT_REQUEST_DOCS;
  return sorted(list).filter((d) => d && d.enabled !== false);
};

export const docByKey = (catalog, key) =>
  (catalog || []).find((d) => d && d.key === key) || null;

// Tope efectivo de una entrada: el catálogo puede BAJAR el límite, nunca subirlo
// por encima del tope duro de la constante.
export const maxBytesFor = (docDef) => {
  const hard =
    docDef && docDef.key === "report"
      ? PAYMENT_REQUEST_MAX_REPORT_BYTES
      : PAYMENT_REQUEST_MAX_FILE_BYTES;
  const configured = Number(docDef && docDef.maxBytes);
  return configured > 0 ? Math.min(configured, hard) : hard;
};

export const formatBytes = (bytes) => {
  const mb = Number(bytes) / (1024 * 1024);
  return (mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10) + " MB";
};

// Motivo por el que un archivo no puede aceptarse, o '' si está bien.
export const getFileRejectReason = (docDef, size) => {
  if (!docDef) return "";
  const limit = maxBytesFor(docDef);
  if (Number(size) > limit) {
    return `El archivo supera el tamaño máximo permitido para ${docDef.label} (${formatBytes(limit)})`;
  }
  return "";
};

// --- Saldo del contrato ------------------------------------------------------
// El valor facturado no puede pasarse del saldo del contrato. La regla es
// BLOQUEANTE en las dos escrituras del valor (guardar el borrador y presentar
// la solicitud): un contrato jurídico no tiene plan de pagos contra el cual
// cuadrar después, así que una factura por encima del valor vigente no es un
// dato que el supervisor pueda "revisar con observación", es un imposible.

export const formatMoney = (value) =>
  "$" + (Number(value) || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 });

// Lo que las DEMÁS solicitudes del contrato ya comprometieron: aprobadas y en
// trámite (presentadas o en corrección). Los borradores NO comprometen —mismo
// criterio que summarizeExecution en paymentExecution.js—, de modo que dos
// borradores abiertos a la vez no se bloquean entre sí.
export const committedByOtherRequests = (requests = [], requestId = null) =>
  (requests || [])
    .filter((r) => r && r.requestId !== requestId)
    .filter((r) => COMMITTING_STATUSES.includes(r.status))
    .reduce((acc, r) => acc + (Number(r.invoiceAmount) || 0), 0);

// Saldo que le queda al contrato para ESTA solicitud, o null si no se conoce su
// valor vigente: sin ese dato no hay techo que imponer y no se bloquea a nadie.
export const getAvailableBudget = ({ budget, requests = [], requestId = null } = {}) => {
  const total = Number(budget);
  if (!Number.isFinite(total) || total <= 0) return null;
  return total - committedByOtherRequests(requests, requestId);
};

// Motivo por el que el valor facturado no sirve, o '' si sirve. Cubre las dos
// razones: que falte y que se pase del saldo.
export const getInvoiceAmountBlockReason = ({ invoiceAmount, availableBudget = null } = {}) => {
  const available = Number(availableBudget);
  // Sin dato del saldo no hay techo que imponer (contratos sin valor vigente
  // conocido): la regla se limita entonces a exigir el valor.
  const hasCeiling =
    availableBudget !== null && availableBudget !== undefined && Number.isFinite(available);

  // Un contrato sin saldo se dice ANTES de pedir el valor: no existe ninguno
  // que sirva, así que "ingresa el valor" sería mandar a un callejón sin salida.
  if (hasCeiling && available <= 0) {
    return "El contrato no tiene saldo disponible: su valor vigente ya está facturado en su totalidad";
  }

  const amount = Number(invoiceAmount);
  if (!amount || Number.isNaN(amount) || amount <= 0) {
    return "Ingresa el valor de la factura antes de enviar al supervisor";
  }

  if (hasCeiling && amount > available) {
    return `El valor facturado (${formatMoney(amount)}) supera el saldo disponible del contrato (${formatMoney(available)}). Corrígelo para poder continuar`;
  }
  return "";
};

// --- Bloqueo del envío -------------------------------------------------------
// Devuelve el motivo por el que NO se puede enviar la solicitud al supervisor,
// o '' si puede enviarse. Mismo patrón que getSubmitForReviewBlockReason del
// flujo natural: el string alimenta directamente el Tooltip del botón, así que
// debe nombrar con precisión lo que falta.
export const getPaymentRequestBlockReason = ({
  documents = {},
  annexes = [],
  invoiceAmount,
  rutDocument = null,
  catalog,
  availableBudget = null,
  today = isoToday(),
} = {}) => {
  const docs = resolvePaymentRequestDocs(catalog);

  const amountReason = getInvoiceAmountBlockReason({ invoiceAmount, availableBudget });
  if (amountReason) return amountReason;

  for (const docDef of docs) {
    if (docDef.required === false || docDef.source === "entity") continue;
    const file = documents[docDef.key];
    if (!file || !file.url) return `Carga ${docDef.label} antes de enviar al supervisor`;
    const reject = getFileRejectReason(docDef, file.size);
    if (reject) return reject;
  }

  // Documentos que se resuelven desde la entidad. Hoy solo el RUT; se recorre el
  // catálogo para que agregar otro no exija tocar esta función.
  for (const docDef of docs) {
    if (docDef.source !== "entity" || docDef.required === false) continue;
    if (!rutDocument || !rutDocument.url) {
      return `Tu entidad no tiene ${docDef.label} registrado. Cárgalo para poder enviar la solicitud`;
    }
    if (!isRutValid(rutDocument.issuedAt, today)) {
      return `El ${docDef.label} de tu entidad no está vigente (debe haberse expedido dentro de los últimos ${RUT_VALIDITY_MONTHS} meses). Actualízalo para poder enviar la solicitud`;
    }
  }

  if ((annexes || []).length > PAYMENT_REQUEST_MAX_ANNEXES) {
    return `Puedes adjuntar máximo ${PAYMENT_REQUEST_MAX_ANNEXES} anexos`;
  }

  return "";
};

// --- Datos de la factura -----------------------------------------------------
// La fecha de emisión y la de vencimiento son INFORMATIVAS (daily 2026-09-14):
// no condicionan el envío ni ningún estado. Se valida solo el formato.
const INVOICE_DATE_FIELDS = {
  invoiceIssuedAt: "La fecha de emisión de la factura",
  invoiceDueAt: "La fecha de vencimiento de la factura",
};

const isBlank = (value) => value === undefined || value === null || value === "";

export const getInvoiceDatesBlockReason = (fields = {}) => {
  for (const key of Object.keys(INVOICE_DATE_FIELDS)) {
    const value = fields[key];
    if (isBlank(value)) continue;
    if (!isValidDate(value)) return `${INVOICE_DATE_FIELDS[key]} no es válida`;
  }
  return "";
};

// --- Recorrido financiero ----------------------------------------------------
// Espejo de la sección homónima del servidor. Después de APPROVED la factura
// recorre cinco áreas de la Dirección Financiera (acta #3); cada etapa es un
// CHECK sin datos obligatorios (daily 2026-09-14) y el responsable se resuelve
// por PERMISO de área.

export const PAYMENT_FLOW_PERMISSION = PERMISSIONS.PAYMENT_FLOW;
const P = PAYMENT_FLOW_PERMISSION;

export const RETURN_TARGET_STATUS = S.IN_CAUSATION;

export const PAYMENT_STAGES = [
  {
    status: S.APPROVED, area: "causation", label: "Causación", permission: P.CAUSATION,
    advanceTo: S.IN_CAUSATION, advanceAction: "RECEIVE", advanceLabel: "Recibir en Causación",
    canReturn: false, optional: [],
  },
  {
    status: S.IN_CAUSATION, area: "causation", label: "Causación", permission: P.CAUSATION,
    advanceTo: S.IN_ACCOUNTING, advanceAction: "ADVANCE", advanceLabel: "Enviar a Contabilidad",
    canReturn: false, optional: [],
  },
  {
    status: S.IN_ACCOUNTING, area: "accounting", label: "Contabilidad", permission: P.ACCOUNTING,
    advanceTo: S.IN_BUDGET, advanceAction: "ADVANCE", advanceLabel: "Enviar a Presupuesto",
    canReturn: true, optional: [],
  },
  {
    status: S.IN_BUDGET, area: "budget", label: "Presupuesto", permission: P.BUDGET,
    advanceTo: S.IN_TREASURY, advanceAction: "ADVANCE", advanceLabel: "Enviar a Tesorería",
    canReturn: true, optional: [],
  },
  {
    status: S.IN_TREASURY, area: "treasury", label: "Tesorería", permission: P.TREASURY,
    advanceTo: S.PAYMENT_NOTIFIED, advanceAction: "ADVANCE", advanceLabel: "Notificar pago",
    canReturn: true, optional: ["paidAt", "reference"],
  },
  {
    status: S.PAYMENT_NOTIFIED, area: "cashier", label: "Caja", permission: P.CASHIER,
    advanceTo: S.PAID, advanceAction: "ADVANCE", advanceLabel: "Confirmar pago",
    canReturn: false, optional: ["proof"],
  },
];

export const PAYMENT_AREAS = [
  { key: "causation", label: "Causación", permission: P.CAUSATION, statuses: [S.APPROVED, S.IN_CAUSATION] },
  { key: "accounting", label: "Contabilidad", permission: P.ACCOUNTING, statuses: [S.IN_ACCOUNTING] },
  { key: "budget", label: "Presupuesto", permission: P.BUDGET, statuses: [S.IN_BUDGET] },
  { key: "treasury", label: "Tesorería", permission: P.TREASURY, statuses: [S.IN_TREASURY] },
  { key: "cashier", label: "Caja", permission: P.CASHIER, statuses: [S.PAYMENT_NOTIFIED, S.PAID] },
];

export const getStageOf = (status) => PAYMENT_STAGES.find((st) => st.status === status) || null;
export const getAreaOf = (status) => PAYMENT_AREAS.find((a) => a.statuses.includes(status)) || null;

// Permiso del área que RECIBE una transición (toPermission en financial.history):
// lo único que C11 necesita para saber a quién avisar.
export const permissionForStatus = (status) => getAreaOf(status)?.permission || null;

const hasStagePermission = (stage, actorPermissions, isAdmin) =>
  !!isAdmin || (Array.isArray(actorPermissions) && actorPermissions.includes(stage.permission));

// Motivo por el que el actor NO puede ejecutar `action` ('RECEIVE' | 'ADVANCE' |
// 'RETURN') sobre la solicitud, o '' si puede. Alimenta el tooltip del botón.
export const getStageActionBlockReason = ({
  request,
  action = "ADVANCE",
  actorPermissions = [],
  isAdmin = false,
  payload = {},
  today = isoToday(),
} = {}) => {
  if (!request) return "La solicitud no existe";
  if (request.status === S.PAID) return "La solicitud ya está pagada";

  const stage = getStageOf(request.status);
  if (!stage) return "La solicitud todavía no ha sido aprobada por el supervisor";

  if (!hasStagePermission(stage, actorPermissions, isAdmin)) {
    return `La solicitud está en ${stage.label}; solo esa área puede actuar sobre ella`;
  }

  if (action === "RETURN") {
    if (!stage.canReturn) return `${stage.label} no devuelve solicitudes`;
    const message = typeof payload.message === "string" ? payload.message.trim() : "";
    if (!message) return "Escribe el motivo de la devolución";
    return "";
  }

  if (action !== "ADVANCE" && action !== "RECEIVE") return "Acción desconocida";
  if (action === "RECEIVE" && stage.advanceAction !== "RECEIVE") {
    return "La solicitud ya fue recibida por Causación";
  }
  if (action === "ADVANCE" && stage.advanceAction === "RECEIVE") {
    return "Causación debe recibir la solicitud antes de enviarla";
  }

  const data = payload || {};
  if (!isBlank(data.paidAt)) {
    if (!stage.optional.includes("paidAt")) return "Esta etapa no registra la fecha del pago";
    if (!isValidDate(data.paidAt)) return "La fecha del pago no es válida";
    if (data.paidAt > today) return "La fecha del pago no puede ser futura";
  }
  if (!isBlank(data.reference)) {
    if (!stage.optional.includes("reference")) return "Esta etapa no registra la referencia del pago";
    if (typeof data.reference !== "string") return "La referencia del pago no es válida";
  }
  if (!isBlank(data.proof)) {
    if (!stage.optional.includes("proof")) return "Esta etapa no adjunta comprobante";
    if (!data.proof?.url) return "El comprobante no tiene archivo";
  }
  return "";
};

// --- Revisión ----------------------------------------------------------------
// Espejo de la sección homónima del lado servidor. Dónde vive la revisión:
//   documentos del catálogo → documents[key].review
//   anexos libres           → annexes[i].review
// El RUT es el caso especial: el ARCHIVO se resuelve desde la entidad y vive en
// rutDocument, pero su REVISIÓN se guarda igual en documents.rut.review, para
// que exista un solo lugar donde buscar el estado de revisión de un documento
// del catálogo.

export const reviewStatusOf = (entry) => entry?.review?.status || REVIEW_STATUS.PENDING;

// Claves obligatorias que se le exigieron a ESTA solicitud al presentarla. El
// catálogo es administrable: si alguien vuelve obligatorio un documento nuevo
// después de que la empresa presentó, esa solicitud no puede quedar atrapada
// sin poder aprobarse. Por eso el envío congela `requiredAtSubmit`, y solo se
// cae al catálogo vivo cuando el campo no existe (solicitudes previas a C7).
export const requiredDocKeysFor = (request, catalog) => {
  const frozen = request?.requiredAtSubmit;
  if (Array.isArray(frozen) && frozen.length) return [...frozen];
  return resolvePaymentRequestDocs(catalog)
    .filter((d) => d.required !== false)
    .map((d) => d.key);
};

// Vista uniforme de todo lo revisable: documentos del catálogo y anexos, con su
// etiqueta y su estado. La UI la usa para pintar la fila de cada documento y
// los helpers de abajo para no repetir el recorrido.
export const reviewItemsOf = (request, catalog) => {
  const documents = request?.documents || {};
  const items = resolvePaymentRequestDocs(catalog).map((docDef) => {
    const entry = documents[docDef.key];
    const file = docDef.source === "entity" ? request?.rutDocument : entry;
    return {
      kind: "document",
      key: docDef.key,
      label: docDef.label,
      required: docDef.required !== false,
      present: !!file?.url,
      status: reviewStatusOf(entry),
    };
  });

  (request?.annexes || []).forEach((annex, index) => {
    items.push({
      kind: "annex",
      key: `annex:${index}`,
      label: annex?.name || `Anexo ${index + 1}`,
      required: false,
      present: !!annex?.url,
      status: reviewStatusOf(annex),
    });
  });

  return items;
};

const listLabels = (items) => items.map((i) => i.label).join(", ");

// Motivo por el que NO se puede aprobar la solicitud, o '' si puede aprobarse.
export const getApprovalBlockReason = ({ request, catalog } = {}) => {
  if (!request) return "La solicitud no existe";

  // Aprobada o ya en el recorrido financiero: no hay nada que aprobar.
  if (isPostApproval(request.status)) return "La solicitud ya fue aprobada";
  if (request.status === PAYMENT_REQUEST_STATUS.IN_CORRECTION) {
    return "La solicitud está en corrección por la empresa";
  }
  if (request.status !== PAYMENT_REQUEST_STATUS.SUBMITTED) {
    return "La solicitud todavía no ha sido presentada";
  }

  const items = reviewItemsOf(request, catalog);

  // Nada devuelto, sea obligatorio, opcional o anexo: un documento devuelto
  // significa que la empresa todavía tiene trabajo pendiente.
  const returned = items.filter((i) => i.status === REVIEW_STATUS.RETURNED);
  if (returned.length) {
    return `Hay documentos devueltos pendientes de corrección: ${listLabels(returned)}`;
  }

  // Solo los obligatorios: exigir que el supervisor marque cada anexo libre
  // convertiría la aprobación en un trámite.
  const requiredKeys = requiredDocKeysFor(request, catalog);
  const unreviewed = items.filter(
    (i) => requiredKeys.includes(i.key) && i.status !== REVIEW_STATUS.REVIEWED
  );
  if (unreviewed.length) return `Falta marcar como revisado: ${listLabels(unreviewed)}`;

  return "";
};

// Durante la corrección la empresa solo toca lo que le devolvieron. Cambiar un
// documento que el supervisor no señaló sería alterar en silencio una solicitud
// ya revisada; si hace falta, el supervisor lo devuelve también.
export const canEditDocumentDuringCorrection = (request, key) => {
  if (request?.status !== PAYMENT_REQUEST_STATUS.IN_CORRECTION) return false;
  const item = reviewItemsOf(request, null).find((i) => i.key === key);
  return item?.status === REVIEW_STATUS.RETURNED;
};

// Motivo por el que el supervisor NO puede cerrar la revisión notificando las
// devoluciones, o '' si puede.
export const getReturnsNotifyBlockReason = ({ request, catalog } = {}) => {
  if (!request) return "La solicitud no existe";
  if (request.status !== PAYMENT_REQUEST_STATUS.SUBMITTED) {
    return "Solo se pueden notificar devoluciones de una solicitud presentada";
  }
  const returned = reviewItemsOf(request, catalog).filter(
    (i) => i.status === REVIEW_STATUS.RETURNED
  );
  if (!returned.length) return "Devuelve al menos un documento antes de notificar";
  return "";
};

// Motivo por el que la empresa NO puede cerrar su ciclo de corrección, o ''.
export const getCorrectionNotifyBlockReason = ({ request, catalog } = {}) => {
  if (!request) return "La solicitud no existe";
  if (request.status !== PAYMENT_REQUEST_STATUS.IN_CORRECTION) {
    return "La solicitud no está en corrección";
  }
  const pending = reviewItemsOf(request, catalog).filter(
    (i) => i.status === REVIEW_STATUS.RETURNED
  );
  if (pending.length) {
    return `Reemplaza los documentos devueltos antes de notificar la corrección: ${listLabels(pending)}`;
  }
  return "";
};
